<script setup>
/* global axios */
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useAccount } from 'dashboard/composables/useAccount';
import ConversationApi from 'dashboard/api/inbox/conversation';

ChartJS.register(ArcElement, Tooltip, Legend);

const { accountId } = useAccount();

const STAGES = [
  { key: 'nuevo-contacto', label: 'Nuevo Contacto', description: 'Leads que ingresaron por primera vez', icon: 'i-lucide-user-plus', color: '#76b658' },
  { key: 'seguimiento-efectivo', label: 'Seguimiento Efectivo', description: 'Respondieron con interes real', icon: 'i-lucide-check-circle-2', color: '#9dc353' },
  { key: 'seguimiento-no-efectivo', label: 'Seg. No Efectivo', description: 'Sin respuesta o negativa', icon: 'i-lucide-clock-4', color: '#f59e0b' },
  { key: 'agenda-posible', label: 'Agenda Posible', description: 'Interesados en avanzar', icon: 'i-lucide-calendar-check', color: '#3b82f6' },
  { key: 'contacto-caido', label: 'Contacto Caido', description: 'Perdio interes definitivamente', icon: 'i-lucide-user-x', color: '#ef4444' },
];

const counts = ref([0, 0, 0, 0, 0]);
const loading = ref(true);
const loadingRecent = ref(true);
const lastUpdated = ref('');
const recentConversations = ref([]);
const isDark = ref(document.documentElement.classList.contains('dark'));

const observer = new MutationObserver(() => {
  isDark.value = document.documentElement.classList.contains('dark');
});

onMounted(async () => {
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  await Promise.all([fetchCounts(), fetchRecentLabeled()]);
});

onUnmounted(() => observer.disconnect());

const total = computed(() => counts.value.reduce((a, b) => a + b, 0));
const totalActive = computed(() => counts.value.slice(0, 4).reduce((a, b) => a + b, 0));

const conversionRate = computed(() => {
  const nuevo = counts.value[0] ?? 0;
  const agenda = counts.value[3] ?? 0;
  if (!nuevo) return 0;
  return Math.round((agenda / nuevo) * 100);
});

const doughnutData = computed(() => ({
  labels: STAGES.map(s => s.label),
  datasets: [{
    data: counts.value,
    backgroundColor: STAGES.map(s => s.color),
    borderWidth: 2,
    borderColor: isDark.value ? '#1a1f2e' : '#ffffff',
    hoverOffset: 8,
  }],
}));

const doughnutOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 400 },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: ctx => ` ${ctx.label}: ${ctx.raw}`,
      },
    },
  },
  cutout: '62%',
}));

async function fetchCounts() {
  loading.value = true;
  const results = await Promise.all(
    STAGES.map(s =>
      ConversationApi.meta({ labels: s.key })
        .then(r => r.data?.meta?.all_count ?? 0)
        .catch(() => 0)
    )
  );
  counts.value = results;
  lastUpdated.value = new Date().toLocaleTimeString('es-AR');
  loading.value = false;
}

async function fetchRecentLabeled() {
  loadingRecent.value = true;
  const allConvs = await Promise.all(
    STAGES.map((s, i) =>
      axios.get(`/api/v1/accounts/${accountId.value}/conversations`, {
        params: { labels: s.key, page: 1 },
      })
        .then(r => (r.data?.data?.payload ?? []).slice(0, 4).map(c => ({ ...c, _si: i })))
        .catch(() => [])
    )
  );
  recentConversations.value = allConvs
    .flat()
    .sort((a, b) => b.last_activity_at - a.last_activity_at)
    .slice(0, 8);
  loadingRecent.value = false;
}

async function refresh() {
  await Promise.all([fetchCounts(), fetchRecentLabeled()]);
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - ts * 1000) / 60000);
  if (diff < 60) return `hace ${diff}m`;
  if (diff < 1440) return `hace ${Math.floor(diff / 60)}h`;
  return `hace ${Math.floor(diff / 1440)}d`;
}
</script>

<template>
  <div class="pt-6 pb-12">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">Resumen General</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">Estado actual del pipeline de leads</p>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="lastUpdated" class="text-xs text-n-slate-9">{{ lastUpdated }}</span>
        <button class="text-n-slate-11 hover:text-n-brand transition-colors" @click="refresh">
          <span class="i-lucide-refresh-cw size-4" :class="{ 'animate-spin': loading }" />
        </button>
      </div>
    </div>

    <!-- Top row -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

      <!-- Stage cards + KPIs -->
      <div class="lg:col-span-2 space-y-3">
        <!-- KPIs -->
        <div class="grid grid-cols-2 gap-3">
          <div class="rounded-xl border border-n-weak bg-n-surface-2 p-5">
            <p class="text-xs font-medium text-n-slate-9 uppercase tracking-wide mb-1">Total Activos</p>
            <p class="text-3xl font-bold text-n-slate-12">
              <span v-if="loading" class="inline-block w-12 h-8 bg-n-slate-3 rounded animate-pulse" />
              <span v-else>{{ totalActive }}</span>
            </p>
            <p class="text-xs text-n-slate-9 mt-1">Sin contar contactos caidos</p>
          </div>
          <div class="rounded-xl border border-n-brand/30 bg-n-brand/5 p-5">
            <p class="text-xs font-medium text-n-slate-9 uppercase tracking-wide mb-1">Conversion</p>
            <p class="text-3xl font-bold text-n-brand">
              <span v-if="loading" class="inline-block w-12 h-8 bg-n-slate-3 rounded animate-pulse" />
              <span v-else>{{ conversionRate }}%</span>
            </p>
            <p class="text-xs text-n-slate-9 mt-1">Nuevo → Agenda posible</p>
          </div>
        </div>

        <!-- Stage rows (equal width, no funnel) -->
        <div class="rounded-xl border border-n-weak bg-n-surface-2 overflow-hidden">
          <div
            v-for="(stage, i) in STAGES"
            :key="stage.key"
            class="flex items-center gap-4 px-5 py-3.5 border-b border-n-weak last:border-0"
          >
            <div
              class="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              :style="{ backgroundColor: stage.color + '1a' }"
            >
              <span :class="[stage.icon, 'size-4']" :style="{ color: stage.color }" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12">{{ stage.label }}</p>
              <p class="text-xs text-n-slate-9 truncate">{{ stage.description }}</p>
            </div>
            <div class="flex-shrink-0 flex items-center gap-3">
              <!-- mini bar -->
              <div class="w-20 h-1.5 rounded-full bg-n-slate-4 overflow-hidden hidden sm:block">
                <div
                  class="h-full rounded-full transition-all duration-700"
                  :style="{
                    width: total > 0 ? (counts[i] / total * 100) + '%' : '0%',
                    backgroundColor: stage.color,
                  }"
                />
              </div>
              <p class="text-xs w-8 text-right" :style="{ color: stage.color }">
                {{ total > 0 ? Math.round((counts[i] ?? 0) / total * 100) : 0 }}%
              </p>
              <p class="text-xl font-bold text-n-slate-12 w-8 text-right">
                <span v-if="loading" class="inline-block w-7 h-6 bg-n-slate-3 rounded animate-pulse" />
                <span v-else>{{ counts[i] ?? 0 }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Donut chart -->
      <div class="rounded-xl border border-n-weak bg-n-surface-2 p-5 flex flex-col">
        <p class="text-sm font-semibold text-n-slate-11 mb-4">Distribucion</p>
        <div class="relative flex-1 min-h-[180px]">
          <div v-if="loading || !total" class="absolute inset-0 flex items-center justify-center">
            <span v-if="loading" class="i-lucide-loader-2 size-7 text-n-slate-9 animate-spin" />
            <div v-else class="text-center">
              <span class="i-lucide-chart-pie size-10 text-n-slate-5 block mx-auto mb-2" />
              <p class="text-xs text-n-slate-8">Sin datos</p>
            </div>
          </div>
          <div v-else class="relative h-full">
            <Doughnut :data="doughnutData" :options="doughnutOptions" />
            <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p class="text-2xl font-bold text-n-slate-12">{{ total }}</p>
              <p class="text-xs text-n-slate-9">leads</p>
            </div>
          </div>
        </div>
        <!-- Legend -->
        <div class="mt-4 space-y-1.5">
          <div v-for="(stage, i) in STAGES" :key="stage.key" class="flex items-center gap-2 text-xs">
            <span class="inline-block w-2 h-2 rounded-full flex-shrink-0" :style="{ backgroundColor: stage.color }" />
            <span class="text-n-slate-10 flex-1 truncate">{{ stage.label }}</span>
            <span class="font-semibold text-n-slate-11">{{ counts[i] ?? 0 }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent labeled conversations -->
    <div class="rounded-xl border border-n-weak bg-n-surface-2 overflow-hidden">
      <div class="flex items-center justify-between px-5 py-4 border-b border-n-weak">
        <h2 class="text-sm font-semibold text-n-slate-12">Ultimas etiquetas agregadas</h2>
        <span v-if="loadingRecent" class="i-lucide-loader-2 size-4 text-n-slate-9 animate-spin" />
      </div>

      <div v-if="!loadingRecent && recentConversations.length === 0" class="py-10 text-center">
        <p class="text-sm text-n-slate-9">No hay conversaciones etiquetadas aun</p>
      </div>

      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="conv in recentConversations"
          :key="conv.id"
          class="flex items-center gap-4 px-5 py-3 hover:bg-n-surface-1 transition-colors"
        >
          <div class="w-8 h-8 rounded-full bg-n-brand/15 flex items-center justify-center flex-shrink-0">
            <span class="text-xs font-semibold text-n-brand">
              {{ (conv.meta?.sender?.name ?? '?')[0].toUpperCase() }}
            </span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ conv.meta?.sender?.name ?? 'Sin nombre' }}
            </p>
            <p class="text-xs text-n-slate-9 truncate">
              {{ conv.last_non_activity_message?.content ?? 'Sin mensajes' }}
            </p>
          </div>
          <div class="flex-shrink-0 flex flex-col items-end gap-1">
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium"
              :style="{
                backgroundColor: STAGES[conv._si].color + '22',
                color: STAGES[conv._si].color,
              }"
            >
              {{ STAGES[conv._si].label }}
            </span>
            <span class="text-xs text-n-slate-8">{{ timeAgo(conv.last_activity_at) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
